/* detox-finder-quiz.js — Boost Blenz Detox Finder Quiz.
   Vanilla, no dependencies (matches bb-track.js and the rest of the bb-* build).

   Ported from the design handoff prototype. QUESTIONS / FLOW / INFO / INTROS and
   every scoring weight and reason string below are copied verbatim — they are the
   source of truth and results are fully deterministic (no randomness anywhere in
   scoring; identical answers always produce identical results).

   Backend seams (see CLAUDE_CODE_PROMPT.md "BACKEND INTEGRATION CONTRACT"):
     1. identity   — persistent visitorId (cookie + localStorage), quizId per attempt
     2. events     — batched POST /v1/events, sendBeacon on unload, never blocking
     3. config     — GET /v1/config with 800ms timeout, falls back to baked content
     4. attribution— /cart/add.js line-item properties + /cart/update.js attributes
     5. email      — POST /v1/email (the only endpoint carrying PII)
     6. consent    — hasConsent() gate; no-op today, one line to wire to a CMP

   Hard rule: if the API is unreachable the quiz still works end to end. Every
   network call is fire-and-forget and wrapped; nothing awaits on the UI path. */

(function () {
  'use strict';

  var root = document.querySelector('[data-bbq]');
  if (!root) return;

  var app = root.querySelector('[data-bbq-app]');
  if (!app) return;

  /* ================================================================== config */

  function attr(name, fallback) {
    var v = root.getAttribute(name);
    return v == null || v === '' ? fallback : v;
  }

  // The ONLY endpoint constant. Public ingest host, separate from the
  // Access-gated admin app. Overridable from the theme editor; blank disables
  // all network calls.
  var API_BASE = String(attr('data-api-base', '')).replace(/\/+$/, '');

  var CONFIG = {
    campaign: attr('data-campaign', 'detox-finder'),
    homeUrl: attr('data-home-url', '/'),
    cartUrl: attr('data-cart-url', '/cart'),
    incentiveMode: attr('data-incentive-mode', 'none'),
    incentivePercent: parseInt(attr('data-incentive-percent', '10'), 10) || 10,
    incentiveCode: attr('data-incentive-code', 'DETOX10'),
    shippingThreshold: parseInt(attr('data-shipping-threshold', '50'), 10) || 50,
    forceIntro: attr('data-force-intro', 'auto'),
    remoteConfigEnabled: attr('data-remote-config', 'true') === 'true',
    scoreCap: 8 // raw points that read as a 100% match on the chart
  };

  function readJSON(sel, fallback) {
    try {
      var node = root.querySelector(sel);
      return node ? JSON.parse(node.textContent) : fallback;
    } catch (e) { return fallback; }
  }

  var PRODUCTS = readJSON('[data-bbq-products]', {});
  var ART = readJSON('[data-bbq-art]', {});

  /* ============================================ baked content (the default) */

  var INTROS = [
    { eyebrow: 'Detox Quiz', title: 'Find Your Detox In About 2 Minutes', sub: 'Answer 8 quick questions and we’ll show you exactly which detox — or combo — fits your body and your habits.', note: '8 questions · ~2 min · instant results', cta: 'Start the quiz' },
    { eyebrow: 'Detox Quiz', title: 'Do You Even Need To Detox?', sub: 'No fear-mongering. Answer a few honest questions and we’ll tell you straight — recommending only what actually fits you.', note: '8 questions · ~2 min · see your results free', cta: 'Find out' },
    { eyebrow: 'Detox Quiz', title: 'A Few Questions. We’ll Explain The Rest.', sub: 'Common habits and everyday exposures add up. We’ll walk through yours and explain — in plain English — what your body may be working overtime on.', note: '8 questions · ~2 min · plain-English results', cta: 'Begin' }
  ];

  var THEMES = {
    liver: { bg: '#fff17f', ink: '#6b5400', label: 'Liver & Filtering' },
    parasite: { bg: '#ff5b4a', ink: '#ffffff', label: 'Gut & Parasites' },
    kidney: { bg: '#9ae0f5', ink: '#12484f', label: 'Kidneys & Water' },
    metal: { bg: '#c8c2e8', ink: '#2d2470', label: 'Exposure & Metals' },
    general: { bg: '#3c3291', ink: '#ffffff', label: 'Your Habits' }
  };

  var INFO = {
    basics: { bg: '#3c3291', ink: '#ffffff', illo: ART.illOrganSupport, eyebrow: 'A quick primer', title: 'Your body has a cleanup crew', body: 'Your liver and kidneys filter everything you eat, drink, and breathe. The next few questions are about how hard they’re working.', cta: 'Got it' },
    intake: { bg: '#ff5b4a', ink: '#ffffff', illo: ART.illTargetParasites, eyebrow: 'What goes in', title: 'Some things hitch a ride', body: 'Certain foods and drinks are the most common way parasites and metals get into the body. This isn’t about fear — just how often it happens for you.', cta: 'Makes sense' },
    environment: { bg: '#9ae0f5', ink: '#12484f', illo: ART.illRemoveMetals, eyebrow: 'The world around you', title: 'Exposure adds up quietly', body: 'Your water, your city, even old dental fillings shape your heavy-metal load over time. A few quick ones about your environment.', cta: 'Continue' }
  };

  var QUESTIONS = [
    { id: 'diet', theme: 'liver', image: ART.illOrganSupport,
      caption: 'Your liver is the body’s main filter — what you eat is the biggest factor in how hard it’s working.',
      title: 'How would you describe your everyday diet?', sub: 'Pick the closest fit.', multi: false,
      options: [
        { id: 'whole', label: 'Mostly whole, home-cooked foods', score: {}, reasons: {} },
        { id: 'mixed', label: 'A mix of home cooking and takeout', score: { liver: 1, parasite: 1 }, reasons: { liver: 'a mix of home & takeout meals', parasite: 'eating out regularly' } },
        { id: 'processed', label: 'Lots of packaged or processed foods', score: { liver: 2, metal: 1 }, reasons: { liver: 'a diet high in processed food', metal: 'additives in packaged food' } },
        { id: 'fastfood', label: 'Frequent fast food or dining out', score: { liver: 2, parasite: 2 }, reasons: { liver: 'frequent fast food', parasite: 'food often prepared outside the home' } }
      ] },
    { id: 'drinks', theme: 'liver', image: ART.illOrganSupport,
      caption: 'Alcohol and caffeine both route through the liver and kidneys — regular use means extra shifts.',
      title: 'How’s your alcohol and caffeine?', sub: 'Roughly, on a normal week.', multi: false,
      options: [
        { id: 'heavy', label: 'A few drinks a week and/or daily coffee', score: { liver: 2, kidney: 1 }, reasons: { liver: 'regular alcohol & caffeine', kidney: 'caffeine load on the kidneys' } },
        { id: 'some', label: 'The occasional drink or coffee', score: { liver: 1 }, reasons: { liver: 'some alcohol & caffeine' } },
        { id: 'none', label: 'Rarely or never', score: {}, reasons: {} }
      ] },
    { id: 'rawfoods', theme: 'parasite', image: ART.illTargetParasites,
      caption: 'Certain foods are the most common way parasites and heavy metals get in.',
      title: 'Do any of these sound like you?', sub: 'Select all that apply — or choose none.', multi: true,
      options: [
        { id: 'sushi', label: 'I eat sushi or raw / undercooked fish', score: { parasite: 2, metal: 1 }, reasons: { parasite: 'eating raw or undercooked fish', metal: 'raw fish, a common mercury source' } },
        { id: 'meat', label: 'I eat undercooked meat or street food', score: { parasite: 2 }, reasons: { parasite: 'undercooked meat or street food' } },
        { id: 'bigfish', label: 'I eat large fish (tuna, swordfish) often', score: { metal: 2 }, reasons: { metal: 'regular large predatory fish' } },
        { id: 'none', label: 'None of these', exclusive: true, score: {}, reasons: {} }
      ] },
    { id: 'water', theme: 'kidney', image: ART.illRemoveMetals,
      caption: 'Water is how your kidneys flush everything out. Low intake makes that job harder.',
      title: 'How much water do you drink on a normal day?', sub: 'Your best guess is fine.', multi: false,
      options: [
        { id: 'low', label: 'Less than 4 cups', score: { kidney: 2 }, reasons: { kidney: 'low daily water intake' } },
        { id: 'mid', label: 'Around 4–8 cups', score: { kidney: 1 }, reasons: { kidney: 'moderate water intake' } },
        { id: 'high', label: '8+ cups', score: {}, reasons: {} }
      ] },
    { id: 'symptoms', theme: 'parasite', image: ART.illTargetParasites,
      caption: 'These are the everyday signals people notice when the body is carrying more than it can easily clear.',
      title: 'Which of these do you notice regularly?', sub: 'Select all that apply — or none.', multi: true,
      options: [
        { id: 'digestion', label: 'Bloating, gas, or irregular digestion', score: { parasite: 2, liver: 1 }, reasons: { parasite: 'ongoing digestive issues', liver: 'sluggish digestion' } },
        { id: 'energy', label: 'Low energy or brain fog', score: { liver: 2, metal: 1 }, reasons: { liver: 'low energy & fatigue', metal: 'brain fog' } },
        { id: 'cravings', label: 'Strong sugar or carb cravings', score: { parasite: 2 }, reasons: { parasite: 'persistent sugar cravings' } },
        { id: 'skin', label: 'Skin breakouts or rashes', score: { liver: 1, parasite: 1 }, reasons: { liver: 'skin flare-ups', parasite: 'skin issues' } },
        { id: 'focus', label: 'Trouble with focus or memory', score: { metal: 2 }, reasons: { metal: 'trouble concentrating' } },
        { id: 'none', label: 'None really', exclusive: true, score: {}, reasons: {} }
      ] },
    { id: 'exposure', theme: 'metal', image: ART.illRemoveMetals,
      caption: 'Where you live and what you’re around quietly shapes your heavy-metal and parasite exposure.',
      title: 'Anything here part of your daily life?', sub: 'Select all that apply — or none.', multi: true,
      options: [
        { id: 'tap', label: 'I mostly drink tap water', score: { metal: 2, kidney: 1 }, reasons: { metal: 'drinking tap water', kidney: 'mineral load from tap water' } },
        { id: 'fillings', label: 'I have older metal dental fillings', score: { metal: 2 }, reasons: { metal: 'older metal fillings' } },
        { id: 'city', label: 'I live in a city, near industry, or farmland', score: { metal: 1 }, reasons: { metal: 'environmental exposure near home' } },
        { id: 'pets', label: 'I have pets or am around animals a lot', score: { parasite: 2 }, reasons: { parasite: 'close contact with animals' } },
        { id: 'travel', label: 'I’ve traveled abroad in the past year', score: { parasite: 2 }, reasons: { parasite: 'recent international travel' } },
        { id: 'none', label: 'None of these', exclusive: true, score: {}, reasons: {} }
      ] },
    { id: 'history', theme: 'general', image: ART.illBindRemove,
      caption: 'If you’ve never done a full detox, starting at the beginning — in the right order — makes a real difference.',
      title: 'Have you ever done a full detox before?', sub: 'This shapes where we’d start you.', multi: false,
      options: [
        { id: 'never', label: 'Never — this would be my first', never: true, kit: 3, score: {}, reasons: {} },
        { id: 'once', label: 'Once or twice, a while ago', kit: 1, score: {}, reasons: {} },
        { id: 'regular', label: 'Yes, I detox regularly', score: {}, reasons: {} }
      ] },
    { id: 'goal', theme: 'general', image: ART.illBindRemove,
      caption: 'Last one. This helps us lead with what matters most to you right now.',
      title: 'What matters most to you right now?', sub: 'Pick your top priority.', multi: false,
      options: [
        { id: 'reset', label: 'A full reset — start clean', goal: 'reset', score: {}, reasons: {} },
        { id: 'energy', label: 'More energy day to day', goal: 'energy', score: { liver: 1 }, reasons: { liver: 'wanting more daily energy' } },
        { id: 'digestion', label: 'Better digestion and gut health', goal: 'digestion', score: { parasite: 1 }, reasons: { parasite: 'a focus on gut health' } },
        { id: 'exposure', label: 'Clearing what I’ve been exposed to', goal: 'exposure', score: { metal: 1 }, reasons: { metal: 'wanting to clear past exposure' } },
        { id: 'curious', label: 'Honestly, just seeing if I need it', goal: 'curious', score: {}, reasons: {} }
      ] }
  ];

  var FLOW = [
    { type: 'info', key: 'basics' },
    { type: 'q', qid: 'diet' },
    { type: 'q', qid: 'drinks' },
    { type: 'info', key: 'intake' },
    { type: 'q', qid: 'rawfoods' },
    { type: 'q', qid: 'water' },
    { type: 'q', qid: 'symptoms' },
    { type: 'info', key: 'environment' },
    { type: 'q', qid: 'exposure' },
    { type: 'q', qid: 'history' },
    { type: 'q', qid: 'goal' }
  ];

  // The area words spliced into the {areas} token. Read from UI at call time,
  // not captured in a const, so a published edit actually takes effect.
  function areaWord(key) {
    return UI['areaWord' + key.charAt(0).toUpperCase() + key.slice(1)] || key;
  }
  var AREA_NAME = { liver: 'Liver Cleanse', kidney: 'Kidney Cleanse', parasite: 'Parasite Killer', metal: 'Heavy Metal Detox' };

  var DISCLAIMER = 'These statements have not been evaluated by the Food & Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease. This quiz is for educational purposes only and is not medical advice. Consult your healthcare provider before beginning any detox program, especially if you are pregnant, nursing, taking medication, or have a medical condition. © Boost Blenz · Blended in Golden, CO.';

  // User-facing copy, overridable from the admin's `ui` config block. Seeded with
  // the literals that were previously inline, so an absent/partial `ui` is a no-op.
  // Must stay `var` — reassigned key-by-key inside applyRemoteConfig().
  var UI = {
    backLabel: 'Back',
    continueLabel: 'Continue',
    lastStepLabel: 'See my results',
    progressWord: 'Question',                 // "Question 3 of 8"
    exitTitle: 'Are you sure?',
    exitBody: 'Your answers won’t be saved, and you’d start over next time.',
    exitStay: 'No, keep going',
    exitRestart: 'I want to start the quiz over',
    exitLeave: 'Leave the quiz, I’m done',
    emailCta: 'Email me my results',
    emailHide: 'Hide form',
    emailHeading: 'Email me my results',
    emailIntro: 'Keep your plan handy. We’ll send these recommendations and buy links to your inbox so you can decide later without retaking the quiz.',
    emailButton: 'Email me my results',
    emailFirstLabel: 'First name',
    emailEmailLabel: 'Email',
    emailPhoneLabel: 'Mobile',
    emailPhoneHint: '(optional — for text reminders)',
    emailConsent: 'Keep me posted with detox tips and occasional offers. Your results email sends either way — this just adds you to the list.',
    emailConfirmTitle: 'On its way!',
    emailConfirmPrefix: 'We just sent your detox plan and buy links to ',
    emailConfirmSuffix: '. It’s saved to your name, so you won’t need to retake the quiz.',
    emailConfirmFallback: 'your inbox',
    addToCart: 'Add to cart',
    addedLabel: 'Added',
    viewProduct: 'Visit product',
    startOver: 'Start over',
    topMatch: 'Top match',
    recsLabel: 'Recommended for you, in order',

    // Results-page section headings. These were inline in renderResults, which
    // meant the admin had no way to reach them — they were the only visible
    // strings on that screen that weren't editable. Anything in UI is picked up
    // by applyRemoteConfig automatically, so adding them here is what puts them
    // in the Content builder.
    resultsMatchLabel: 'How strongly each detox matched your answers',
    scoreHowLabel: 'How the score works',
    scoreHowText: 'Every answer adds points to the detox areas it signals — the more an answer points to buildup in an area, the more it adds. We total the points per area, then show each as a percentage of the highest possible score. Your top match is simply the area with the most points.',
    profileEyebrow: 'Your detox profile',
    profileLabel: 'What we’re seeing',

    // ---- Results copy that used to be hard-coded inside computeResults() ----
    // This is the page's persuasive copy: the big title, the "What this means"
    // paragraph, and the one-line reason under every recommended product. It was
    // unreachable from the admin, which also meant it was unreachable in the
    // results email (the email renders what this page sends — see the 1:1 payload
    // below). Moving it into UI makes both editable in one edit.
    //
    // TOKENS: {areas} is replaced with the shopper's own area words joined
    // naturally ("gut & digestion and liver"), {amount} with the live kit saving,
    // {products} with the detox names the binder pairs with. Keep the token in the
    // string — drop it and that sentence simply stops being personalized. A token
    // with no value collapses cleanly, so an over-edited string can't render
    // "your ." at the customer.
    areaWordLiver: 'liver',
    areaWordKidney: 'kidneys',
    areaWordParasite: 'gut & digestion',
    areaWordMetal: 'heavy-metal load',

    // Big headline, one per branch of the result.
    titleDefault: 'Your Detox Plan',
    titleLight: 'You’re In Good Shape',
    titleFirstTimer: 'Start At The Beginning',
    titleFullKit: 'Go For The Full Reset',
    titleTargetedOne: 'Here’s Where To Start',
    titleTargetedMany: 'Your Recommended Stack',

    // The "What this means" paragraph. The two full-kit branches share a common
    // closing sentence (overviewFullTail) so it only has to be edited once.
    overviewLight: 'Honestly? Your answers don’t point to a heavy load right now — no need to overdo it. If you’d like a simple, gentle reset to stay ahead of it, here’s where we’d start. No pressure.',
    overviewFirstTimer: 'Since this would be your first detox and a few areas lit up — mainly your {areas} — we’d start you at the beginning and work through it in the right order.',
    overviewFullKit: 'A few areas stood out — mainly your {areas}. Rather than piece it together, the guided kit walks you through all four in the correct sequence.',
    overviewFullTail: 'Here’s the plan, and exactly why.',
    overviewTargetedOne: 'One thing stood out clearly: your {areas}. No need to overdo it — here’s the one detox we’d start with, and why.',
    overviewTargetedMany: 'A couple of areas stood out — your {areas}. Here’s the order we’d run them in, and exactly why.',

    // Per-product headline, shown under the product name on each rec card.
    whyLiver: 'Your liver is the front-line filter — your answers suggest it’s working overtime, so it’s the right first step.',
    whyKidney: 'Your kidneys handle the flushing, and a few of your answers point to extra strain there.',
    whyParasite: 'Our #1 seller. Several of your answers line up with common ways parasites get in and stick around.',
    whyMetal: 'Everyday exposures add up — this one targets the heavy metals your answers flagged.',
    recLightPrimary: 'A gentle 14-day reset for the body’s main filter — an easy, low-commitment place to begin.',
    recLightBinder: 'Optional companion that helps escort what your liver loosens up out of the body.',
    recKit: 'All four detoxes plus The Binder in the correct order — the complete reset, and the best value. It covers everything below in one guided protocol.',
    recStartSmaller: 'Prefer to ease in? Start with just this one — it’s the single area that stood out most for you.',
    recBinder: 'Take it alongside your detox — it binds what’s been loosened up so it leaves the body instead of recirculating.',
    recKitUpsell: 'Doing more than one anyway? The full kit bundles all four detoxes and The Binder in the right order — and costs less than buying them apart.',

    // Reason chips that aren't tied to a specific answer. (The per-answer chips
    // stay where they are — on each answer option in the Content builder.)
    chipLightReset: 'a light maintenance reset',
    chipBinderPairsLiver: 'pairs with the Liver Cleanse',
    chipBinderPairs: 'pairs with {products}',
    chipKitSaves: 'saves ${amount} vs. buying separately',
    chipCoversAll: 'covers all four areas',
    chipUpsellSaves: 'saves ${amount}',

    disclaimer: DISCLAIMER
  };

  // Fills {token} placeholders in editable copy. Unknown tokens are left alone
  // (a typo shows up as a visible {typo} rather than silently deleting text);
  // a token whose value is empty collapses along with one adjoining space, so
  // "your {areas}." can never render as "your ." on a shopper's screen.
  function fill(str, vars) {
    if (typeof str !== 'string' || !str) return '';
    return str.replace(/ ?\{(\w+)\}/g, function (whole, key) {
      if (!Object.prototype.hasOwnProperty.call(vars || {}, key)) return whole;
      var v = vars[key];
      if (v == null || v === '') return '';
      return (whole.charAt(0) === ' ' ? ' ' : '') + v;
    }).trim();
  }

  /* ==================================================== seam 1 — identity */

  function readCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }

  function writeCookie(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      var secure = location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = name + '=' + encodeURIComponent(value) +
        '; expires=' + d.toUTCString() + '; path=/; SameSite=Lax' + secure;
    } catch (e) {}
  }

  function lsGet(k) { try { return window.localStorage.getItem(k) || ''; } catch (e) { return ''; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }
  function ssGet(k) { try { return window.sessionStorage.getItem(k) || ''; } catch (e) { return ''; } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) {} }
  function ssDel(k) { try { window.sessionStorage.removeItem(k); } catch (e) {} }

  function rid(prefix) {
    return prefix + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  // visitorId — persistent, never rotates. Cookie AND localStorage; either can
  // reseed the other so clearing one doesn't split the identity.
  function getVisitorId() {
    var v = lsGet('bb_visitor') || readCookie('bb_visitor');
    if (!v) v = rid('v_');
    lsSet('bb_visitor', v);
    writeCookie('bb_visitor', v, 365);
    return v;
  }

  // quizId — one attempt. Survives a mid-quiz reload; a fresh start mints a new one.
  function getQuizId() {
    var q = ssGet('bb_quiz_id');
    if (!q) { q = rid('q_'); ssSet('bb_quiz_id', q); }
    return q;
  }
  function newQuizId() {
    var q = rid('q_');
    ssSet('bb_quiz_id', q);
    return q;
  }

  var visitorId = getVisitorId();
  var quizId = getQuizId();

  /* ===================================================== seam 6 — consent */

  // Events are first-party and pseudonymous; /v1/email is the only PII path.
  // No consent banner on the store today, so this is a no-op. To gate later,
  // return the CMP's marketing/analytics state here — one line.
  function hasConsent() {
    return true;
  }

  /* ====================================================== seam 2 — events */

  var queue = [];
  var flushTimer = null;
  var FLUSH_MS = 2000;
  var FLUSH_AT = 10;
  var CRITICAL = {
    quiz_completed: 1, recommendation_shown: 1, add_to_cart: 1, add_all_to_cart: 1,
    checkout_click: 1, email_submitted: 1, quiz_abandoned: 1
  };

  // /v1/events is contractually the anonymous endpoint. Sweeping every query param
  // off the landing URL would put whatever a partner or ad platform appended — an
  // email, a click identifier — straight into the analytics table. Allowlist only
  // the marketing params we actually report on, and cap their length.
  var ENTRY_PARAM_ALLOW = /^(utm_[a-z_]+|gclid|fbclid|ttclid|msclkid)$/i;
  var entryParams = {};
  try {
    new URLSearchParams(window.location.search).forEach(function (v, k) {
      if (ENTRY_PARAM_ALLOW.test(k)) entryParams[k] = String(v).slice(0, 200);
    });
  } catch (e) {}

  function envelope(events) {
    return {
      quizId: quizId,
      visitorId: visitorId,
      // The dashboard joins on this. Deliberately location.hostname — where the
      // shopper actually is — not the section's {{ shop.url }}, which can differ
      // on a preview/alternate domain. CONFIG.shopDomain was never read and has
      // been removed so it doesn't look wired up when it isn't.
      shop: location.hostname,
      path: location.pathname,
      entryParams: entryParams,
      sentAt: Date.now(),
      events: events
    };
  }

  function flush(useBeacon) {
    if (!queue.length) return;
    if (!API_BASE || !hasConsent()) { queue.length = 0; return; }

    var batch = queue.splice(0, queue.length);
    var url = API_BASE + '/v1/events';
    var body = '';
    try { body = JSON.stringify(envelope(batch)); } catch (e) { return; }

    try {
      if (useBeacon && navigator.sendBeacon) {
        // quiz_abandoned and checkout_click fire as the page is leaving —
        // they are lost without this.
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        return;
      }
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true,
        credentials: 'omit',
        mode: 'cors'
      })['catch'](function () {});
    } catch (e) {
      // Transport failures must never surface. The quiz ignores them.
    }
  }

  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(function () { flushTimer = null; flush(false); }, FLUSH_MS);
  }

  function track(event, data) {
    var rec = { event: event, quizId: quizId, visitorId: visitorId, t: Date.now() };
    if (data) for (var k in data) if (Object.prototype.hasOwnProperty.call(data, k)) rec[k] = data[k];

    try { (window.BB_QUIZ.events = window.BB_QUIZ.events || []).push(rec); } catch (e) {}
    try {
      window.dataLayer = window.dataLayer || [];
      var dl = { event: 'bb_quiz_' + event };
      for (var j in rec) if (Object.prototype.hasOwnProperty.call(rec, j)) dl[j] = rec[j];
      window.dataLayer.push(dl);
    } catch (e) {}
    try { if (typeof window.BB_QUIZ.onEvent === 'function') window.BB_QUIZ.onEvent(rec); } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', 'bb_quiz_' + event, rec); } catch (e) {}
  }

  // Default transport. A host page can replace window.BB_QUIZ.onEvent before
  // this script runs to take over delivery entirely.
  function defaultOnEvent(rec) {
    queue.push(rec);
    if (CRITICAL[rec.event] || queue.length >= FLUSH_AT) {
      if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
      flush(false);
    } else {
      scheduleFlush();
    }
  }

  window.BB_QUIZ = window.BB_QUIZ || {};
  window.BB_QUIZ.events = window.BB_QUIZ.events || [];
  window.BB_QUIZ.config = CONFIG;
  window.BB_QUIZ.quizId = quizId;
  window.BB_QUIZ.visitorId = visitorId;
  window.BB_QUIZ.entryParams = entryParams;
  if (typeof window.BB_QUIZ.onEvent !== 'function') window.BB_QUIZ.onEvent = defaultOnEvent;

  // `quiz_abandoned` used to fire ONLY from the exit sheet's "Leave the quiz"
  // button, so it recorded the one exit path almost nobody takes. Closing the tab
  // or navigating away — how people actually leave — recorded nothing, and the
  // funnel's abandoned count read ~0 no matter how many sessions were dropped.
  //
  // Fires at most once per page life. Firing early is harmless: the ingest only
  // sets abandoned_at while completed_at IS NULL, so someone who tabs away and
  // comes back to finish is still counted as completed, not abandoned.
  var abandonSent = false;
  function markAbandoned() {
    if (abandonSent || state.screen === 'results') return;
    abandonSent = true;
    track('quiz_abandoned', {
      atStep: state.stepIdx,
      answered: Object.keys(state.answers).length,
      // Distinguishes "bounced off the intro" from "gave up mid-quiz" without
      // needing to join back to the session row.
      screen: state.screen
    });
  }

  function onLeaving() {
    if (state.screen === 'results' && state.resultsAt) {
      track('results_dwell', { qid: null, ms: Date.now() - state.resultsAt });
      state.resultsAt = 0;
    }
    markAbandoned();
    flush(true);
  }
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') onLeaving();
  });
  window.addEventListener('pagehide', onLeaving);

  /* ====================================================== seam 3 — config */

  // Remote config carries image URLs, not bytes — the CDN still serves them.
  // Anything not on an allowlisted origin is rejected and the baked asset wins;
  // this page performs cart operations, so arbitrary URLs are an injection vector.
  var IMG_ALLOWED = ['cdn.shopify.com', 'cdn.shopifycdn.net'];

  function safeImg(url, fallback) {
    if (!url || typeof url !== 'string') return fallback;
    try {
      var u = new URL(url, location.origin);
      if (u.protocol !== 'https:') return fallback;
      if (u.origin === location.origin) return u.href;
      var host = u.hostname.toLowerCase();
      for (var i = 0; i < IMG_ALLOWED.length; i++) {
        if (host === IMG_ALLOWED[i] || host.endsWith('.' + IMG_ALLOWED[i])) return u.href;
      }
      return fallback;
    } catch (e) { return fallback; }
  }

  // Admin-published content addresses artwork by KEY ('illOrganSupport'), because
  // the admin has no way to know this theme's asset URLs — they change on every
  // upload. Baked content already holds resolved URLs. Accept both: a known key
  // wins, anything else falls through to the origin allowlist.
  //
  // Note the fallback is '' at the render sites, not the input. `safeImg(x, x)`
  // returns a rejected value as its own fallback, which made the guard a no-op —
  // an art key resolved same-origin and rendered as a 404 instead of nothing.
  // Absolute https URL, for values that leave the page (the email payload).
  // On-page rendering is fine with protocol-relative; email is not.
  function absUrl(v) {
    if (!v || typeof v !== 'string') return null;
    if (v.indexOf('//') === 0) return 'https:' + v;
    if (v.indexOf('/') === 0) return location.origin + v;
    return v;
  }

  function resolveArt(v, fallback) {
    if (!v || typeof v !== 'string') return fallback;
    if (Object.prototype.hasOwnProperty.call(ART, v) && ART[v]) return ART[v];
    return safeImg(v, fallback);
  }

  // Per-slot art override, set in the Shopify theme editor.
  //
  // The four editorial illustrations are SHARED: eight questions and three info
  // cards point at only four images, so the kidney question renders the
  // "remove metals" art and there was no way to change one without changing the
  // other. ART now also carries one key per slot — 'q_water', 'info_environment'
  // — fed by its own image_picker in the section settings.
  //
  // An untouched picker emits nothing, so we fall straight through to the shared
  // illustration and the screen renders exactly as it did before. The override
  // only exists once someone deliberately uploads one.
  //
  // Deliberately wins over the shared key the admin publishes: the picker is the
  // more specific instruction, and it's the surface the person swapping artwork
  // is actually looking at.
  function slotArt(slot, fallback) {
    if (slot && Object.prototype.hasOwnProperty.call(ART, slot) && ART[slot]) {
      return safeImg(ART[slot], '') || resolveArt(fallback, '');
    }
    return resolveArt(fallback, '');
  }

  // Does this payload try to replace the question set at all?
  function carriesContent(cfg) {
    return !!(cfg && (cfg.questions !== undefined || cfg.flow !== undefined));
  }

  // Reject content that would render a blank screen: every flow step must resolve
  // to real content. The admin can delete a question that a step still points at,
  // so this has to be checked here, not trusted from upstream.
  //
  // Only applies to the question/flow half. A payload carrying just `ui`,
  // `products` or `settings` is a legitimate partial publish (a copy tweak, say)
  // and must still apply — gating those on the presence of questions would make
  // such a publish look successful while silently doing nothing.
  function contentIsSane(cfg) {
    if (!Array.isArray(cfg.questions) || !cfg.questions.length) return false;
    if (!Array.isArray(cfg.flow) || !cfg.flow.length) return false;
    var ids = {};
    for (var i = 0; i < cfg.questions.length; i++) {
      var q = cfg.questions[i];
      // A question with no options makes renderQuestion throw on q.options.map,
      // which kills the whole screen — worse than falling back to baked content.
      if (!q || !q.id || !Array.isArray(q.options) || !q.options.length) return false;
      ids[q.id] = true;
    }
    // A partial publish may hand us a flow that leans on the info cards we
    // already have, so validate against the incoming set falling back to current.
    var info = cfg.info && typeof cfg.info === 'object' ? cfg.info : INFO;
    for (var j = 0; j < cfg.flow.length; j++) {
      var s = cfg.flow[j];
      if (!s) return false;
      if (s.type === 'q') { if (!ids[s.qid]) return false; }
      else if (s.type === 'info') { if (!info[s.key]) return false; }
      else return false;
    }
    return true;
  }

  function applyRemoteConfig(cfg) {
    if (!cfg || typeof cfg !== 'object') return false;

    // If it ships content, that content must be whole. A half-valid publish is
    // rejected outright rather than partly applied.
    var replacingContent = carriesContent(cfg);
    if (replacingContent && !contentIsSane(cfg)) return false;

    try {
      if (replacingContent) {
        QUESTIONS = cfg.questions;
        FLOW = cfg.flow;
      }
      if (Array.isArray(cfg.intros) && cfg.intros.length) INTROS = cfg.intros;
      if (cfg.info && typeof cfg.info === 'object') INFO = cfg.info;
      if (cfg.themes && typeof cfg.themes === 'object') THEMES = cfg.themes;

      // Scoring weights live in the admin, so the denominator that turns them into
      // a percentage has to come from the same place. Out of sync = wrong bars.
      if (cfg.settings && typeof cfg.settings === 'object') {
        var cap = Number(cfg.settings.scoreCap);
        if (cap > 0) CONFIG.scoreCap = cap;
      }

      // Button + email copy. Seeded from UI defaults, so an absent `ui` key is a
      // no-op rather than a page full of blank buttons.
      if (cfg.ui && typeof cfg.ui === 'object') {
        for (var k in cfg.ui) {
          if (!Object.prototype.hasOwnProperty.call(cfg.ui, k)) continue;
          if (typeof cfg.ui[k] === 'string' && cfg.ui[k]) UI[k] = cfg.ui[k];
        }
      }

      // Product/editorial art: remote URL wins, but only from an allowed origin.
      if (cfg.products && typeof cfg.products === 'object') {
        for (var pid in cfg.products) {
          if (!Object.prototype.hasOwnProperty.call(cfg.products, pid)) continue;
          var incoming = cfg.products[pid] || {};
          var current = PRODUCTS[pid] || {};
          if (incoming.name) current.name = incoming.name;
          if (incoming.days) current.days = incoming.days;
          if (incoming.image) current.image = resolveArt(incoming.image, current.image);

          // Price comes from the Shopify product object, never from config.
          // Shopify is what checkout charges; a config price is only a display
          // fallback for a slot with no product wired up. Without this guard the
          // next sale would leave the quiz quoting a price the cart won't honour.
          if (current.priceSource !== 'shopify') {
            if (typeof incoming.price === 'number') current.price = incoming.price;
            if (typeof incoming.compareAt === 'number') current.compareAt = incoming.compareAt;
          }
          PRODUCTS[pid] = current;
        }
      }
      // Never let remote config supply variant IDs — those come from Shopify only.
      return true;
    } catch (e) { return false; }
  }

  // Matches the Worker's edge cache. Without an expiry, one bad publish sticks in
  // a shopper's session for as long as the tab lives.
  var CFG_MEMO_TTL_MS = 5 * 60 * 1000;

  function loadConfig() {
    if (!API_BASE || !CONFIG.remoteConfigEnabled) return;

    var memo = ssGet('bb_quiz_cfg');
    if (memo) {
      var fresh = false;
      try {
        var wrapper = JSON.parse(memo);
        if (wrapper && wrapper.cachedAt && (Date.now() - wrapper.cachedAt) < CFG_MEMO_TTL_MS
            && applyRemoteConfig(wrapper.cfg)) {
          track('config_loaded', { version: (wrapper.cfg && wrapper.cfg.version) || null, source: 'remote' });
          rerenderIfIdle();
          fresh = true;
        }
      } catch (e) {}
      if (fresh) return;
      // Stale, malformed, or rejected — drop it and refetch rather than serving it again.
      try { ssDel('bb_quiz_cfg'); } catch (e) {}
    }

    var done = false;
    var ctrl = null;
    try { ctrl = new AbortController(); } catch (e) {}
    var timer = setTimeout(function () {
      if (done) return;
      done = true;
      try { if (ctrl) ctrl.abort(); } catch (e) {}
      track('config_loaded', { version: null, source: 'baked' });
    }, 800);

    try {
      fetch(API_BASE + '/v1/config?shop=' + encodeURIComponent(location.hostname), {
        credentials: 'omit',
        mode: 'cors',
        signal: ctrl ? ctrl.signal : undefined
      })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (cfg) {
          if (done) return;
          done = true;
          clearTimeout(timer);
          if (cfg && applyRemoteConfig(cfg)) {
            try { ssSet('bb_quiz_cfg', JSON.stringify({ cfg: cfg, cachedAt: Date.now() })); } catch (e) {}
            track('config_loaded', { version: cfg.version || null, source: 'remote' });
            rerenderIfIdle();
          } else {
            track('config_loaded', { version: null, source: 'baked' });
          }
        })
        ['catch'](function () {
          if (done) return;
          done = true;
          clearTimeout(timer);
          track('config_loaded', { version: null, source: 'baked' });
        });
    } catch (e) {
      clearTimeout(timer);
    }
  }

  // Only swap content in if the visitor hasn't started yet — never yank the
  // ground out from under someone mid-quiz.
  function rerenderIfIdle() {
    if (state.screen === 'intro') render();
  }

  /* ======================================================= A/B intro split */

  // Sticky per visitor: derived from visitorId so the same person always sees
  // the same title, without a second stored value.
  function hashStr(s) {
    var h = 0;
    for (var i = 0; i < s.length; i++) { h = ((h << 5) - h + s.charCodeAt(i)) | 0; }
    return Math.abs(h);
  }

  function pickIntroVariant() {
    var forced = CONFIG.forceIntro;
    if (forced && forced !== 'auto') {
      var n = parseInt(forced, 10);
      if (!isNaN(n) && n >= 0 && n < INTROS.length) return n;
    }
    return hashStr(visitorId) % INTROS.length;
  }

  /* ================================================================= state */

  var state = {
    screen: 'intro',
    introVariant: 0,
    stepIdx: 0,
    answers: {},
    added: {},
    cartCount: 0,
    showExit: false,
    firstName: '', email: '', phone: '', consent: true,
    emailError: '', emailSent: false, emailOpen: false,
    startedAt: 0,
    resultsAt: 0,
    cartError: ''
  };

  state.introVariant = pickIntroVariant();

  /* =============================================================== helpers */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Digits only, always two decimals. Split out from money() so the email payload
  // can send the same formatted figure the page shows without the currency symbol
  // (the Klaviyo template supplies its own "$").
  function money2(n) {
    var v = typeof n === 'number' ? n : parseFloat(n);
    return isNaN(v) ? null : v.toFixed(2);
  }

  function money(n) {
    var d = money2(n);
    return d === null ? '' : '$' + d;
  }

  function qById(id) {
    for (var i = 0; i < QUESTIONS.length; i++) if (QUESTIONS[i].id === id) return QUESTIONS[i];
    return null;
  }

  function product(pid) { return PRODUCTS[pid] || {}; }

  function withParams(url) {
    if (!url) return CONFIG.homeUrl;
    var join = url.indexOf('?') >= 0 ? '&' : '?';
    return url + join +
      'utm_source=quiz&utm_medium=onsite&utm_campaign=' + encodeURIComponent(CONFIG.campaign) +
      '&quiz_id=' + encodeURIComponent(quizId);
  }

  // PDP link for the EMAIL, not for on-page clicks.
  //
  // Deliberately carries NO utm_* params. Klaviyo appends its own on send
  // (utm_source=Klaviyo, utm_medium=email, utm_campaign=<flow name>,
  // utm_content=<message name>, utm_id=<flow id>). If we also baked the
  // storefront's onsite set in here, every key would appear twice — and GA4
  // keeps the FIRST occurrence, so `utm_medium=onsite` would win and every
  // click from an email would be logged as an on-site quiz click. Email
  // attribution would read zero while looking perfectly healthy.
  //
  // quiz_id / quiz_rec are NOT tracking params in the UTM sense; they are the
  // keys the orders/create webhook joins on. They stay.
  function emailPdpUrl(pid) {
    var p = product(pid);
    if (!p.url) return CONFIG.homeUrl;
    var join = p.url.indexOf('?') >= 0 ? '&' : '?';
    return p.url + join +
      'quiz_id=' + encodeURIComponent(quizId) +
      '&quiz_rec=' + encodeURIComponent(pid);
  }

  function pdpUrl(pid) {
    var p = product(pid);
    if (!p.url) return CONFIG.homeUrl;
    return withParams(p.url) + '&quiz_rec=' + encodeURIComponent(pid);
  }

  function checkoutUrl() { return withParams(CONFIG.cartUrl); }

  /* ================================== scoring — deterministic, ported exactly */

  function whyLine(pid) {
    return UI['why' + pid.charAt(0).toUpperCase() + pid.slice(1)] || '';
  }

  function rec(pid, rank, headline, reasons, badge, inPlan) {
    return { pid: pid, rank: rank, headline: headline, reasons: reasons || [], badge: badge, inPlan: inPlan };
  }

  // What the kit saves vs. buying its five components separately, computed from
  // live Shopify prices rather than a hard-coded number — otherwise the claim
  // goes stale the first time any of the six prices changes.
  // Returns null (and the claim is dropped entirely) unless every component
  // price is known and the kit is genuinely cheaper. Rounded DOWN so the figure
  // shown is never more than the shopper actually saves.
  var KIT_PARTS = ['liver', 'kidney', 'parasite', 'metal', 'binder'];

  function kitSavings() {
    var kit = product('kit');
    if (!kit || typeof kit.price !== 'number' || !(kit.price > 0)) return null;
    var sum = 0;
    for (var i = 0; i < KIT_PARTS.length; i++) {
      var p = product(KIT_PARTS[i]);
      if (!p || typeof p.price !== 'number' || !(p.price > 0)) return null;
      sum += p.price;
    }
    var diff = Math.floor(sum - kit.price);
    return diff > 0 ? diff : null;
  }

  var planIds = [];

  function computeResults() {
    var a = state.answers;
    var scores = { liver: 0, kidney: 0, parasite: 0, metal: 0 };
    var reasons = { liver: [], kidney: [], parasite: [], metal: [] };
    var kitScore = 0, neverDetox = false, goal = null;

    QUESTIONS.forEach(function (q) {
      var v = a[q.id];
      if (v == null) return;
      var chosen = q.multi ? v : [v];
      chosen.forEach(function (optId) {
        var opt = null;
        for (var i = 0; i < q.options.length; i++) if (q.options[i].id === optId) { opt = q.options[i]; break; }
        if (!opt) return;
        if (opt.score) for (var k in opt.score) if (scores[k] != null) scores[k] += opt.score[k];
        if (opt.reasons) for (var r in opt.reasons) {
          if (reasons[r] && reasons[r].indexOf(opt.reasons[r]) < 0) reasons[r].push(opt.reasons[r]);
        }
        if (opt.kit) kitScore += opt.kit;
        if (opt.never) neverDetox = true;
        if (opt.goal) goal = opt.goal;
      });
    });
    if (goal === 'reset') kitScore += 4;

    var CAP = CONFIG.scoreCap;
    var pct = {};
    Object.keys(scores).forEach(function (k) {
      pct[k] = Math.round(Math.min(scores[k], CAP) / CAP * 100);
    });

    var tie = ['parasite', 'liver', 'kidney', 'metal'];
    var ranked = Object.keys(scores).sort(function (x, y) {
      return scores[y] - scores[x] || tie.indexOf(x) - tie.indexOf(y);
    });
    var flagged = ranked.filter(function (k) { return scores[k] >= 3; });
    var meaningful = ranked.filter(function (k) { return scores[k] >= 2; });

    var kitPrimary = neverDetox
      ? meaningful.length >= 1
      : (flagged.length >= 3 || (goal === 'reset' && meaningful.length >= 2) || kitScore >= 8);

    var recs = [];
    var needLevel = 'targeted';

    function topReasons(keys, n) {
      var out = [];
      keys.forEach(function (k) {
        (reasons[k] || []).forEach(function (r) { if (out.indexOf(r) < 0) out.push(r); });
      });
      return out.slice(0, n || 3);
    }
    function areaList(keys) { return keys.map(areaWord); }
    function joinNat(arr) {
      return arr.length <= 1 ? (arr[0] || '') : arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1];
    }

    var overview = '';
    var resultsTitle = UI.titleDefault;

    if (meaningful.length === 0) {
      needLevel = 'light';
      resultsTitle = UI.titleLight;
      overview = fill(UI.overviewLight, {});
      recs.push(rec('liver', 1, UI.recLightPrimary, [UI.chipLightReset], null, true));
      recs.push(rec('binder', 2, UI.recLightBinder, [UI.chipBinderPairsLiver], null, true));
    } else if (kitPrimary) {
      needLevel = 'full';
      resultsTitle = neverDetox ? UI.titleFirstTimer : UI.titleFullKit;
      var areas = areaList(meaningful.slice(0, 3));
      overview = fill(neverDetox ? UI.overviewFirstTimer : UI.overviewFullKit, { areas: joinNat(areas) }) +
        (UI.overviewFullTail ? ' ' + UI.overviewFullTail : '');
      var savedFull = kitSavings();
      var kitReasons = topReasons(meaningful, 3);
      if (savedFull) kitReasons = kitReasons.concat([fill(UI.chipKitSaves, { amount: savedFull })]);
      recs.push(rec('kit', 1, UI.recKit, kitReasons.slice(0, 4), 'BEST VALUE', true));
      var top = meaningful[0];
      recs.push(rec(top, 2, UI.recStartSmaller, topReasons([top], 2), 'START SMALLER', false));
    } else {
      needLevel = 'targeted';
      resultsTitle = meaningful.length === 1 ? UI.titleTargetedOne : UI.titleTargetedMany;
      var areas2 = areaList(meaningful.slice(0, 2));
      overview = meaningful.length === 1
        ? fill(UI.overviewTargetedOne, { areas: areas2[0] })
        : fill(UI.overviewTargetedMany, { areas: joinNat(areas2) });
      var rank = 1;
      var chosenAreas = [meaningful[0]];
      if (meaningful[1] && scores[meaningful[1]] >= 3) chosenAreas.push(meaningful[1]);
      chosenAreas.forEach(function (pid) {
        recs.push(rec(pid, rank++, whyLine(pid), topReasons([pid], 3), product(pid).badge || null, true));
      });
      var needsBinder = chosenAreas.some(function (p) { return ['liver', 'kidney', 'parasite'].indexOf(p) >= 0; });
      if (needsBinder) {
        var pairNames = chosenAreas.filter(function (p) { return p !== 'metal'; })
          .map(function (p) { return product(p).name; }).join(' & ');
        recs.push(rec('binder', rank++, UI.recBinder, [fill(UI.chipBinderPairs, { products: pairNames })], null, true));
      }
      if (meaningful.length >= 2) {
        var savedUpsell = kitSavings();
        var upsellReasons = [UI.chipCoversAll];
        if (savedUpsell) upsellReasons.push(fill(UI.chipUpsellSaves, { amount: savedUpsell }));
        recs.push(rec('kit', rank++, UI.recKitUpsell, upsellReasons, 'UPGRADE & SAVE', false));
      }
    }

    planIds = recs.filter(function (r) { return r.inPlan; }).map(function (r) { return r.pid; });

    return {
      recs: recs, overview: overview, resultsTitle: resultsTitle,
      needLevel: needLevel, scores: scores, pct: pct, ranked: ranked
    };
  }

  /* ================================================= seam 4 — cart / attribution */

  function attribution(pid) {
    var a = {
      quiz_id: quizId,
      quiz_source: CONFIG.campaign,
      quiz_visitor: visitorId
    };
    if (pid) a.quiz_rec = pid;
    return a;
  }

  function postJSON(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error('cart ' + r.status);
      return r.json();
    });
  }

  // Cart-level attributes survive cart edits; line-item properties survive onto
  // the order line. The dashboard joins on both, so we write both.
  function stampCart(pid) {
    return postJSON('/cart/update.js', { attributes: attribution(pid) })
      .then(function (cart) {
        if (cart && typeof cart.item_count === 'number') {
          state.cartCount = cart.item_count;
        }
        return cart;
      })['catch'](function () {});
  }

  function addItems(items, pid) {
    return postJSON('/cart/add.js', { items: items }).then(function (res) {
      return stampCart(pid).then(function () { return res; });
    });
  }

  function addToCart(pid) {
    var p = product(pid);
    if (!p.variantId) {
      state.cartError = 'That product isn’t available right now.';
      render();
      return;
    }
    state.cartError = '';
    track('add_to_cart', { product: pid, variantId: p.variantId, cartUrl: checkoutUrl() });

    // Prefer the site's shared cart path. window.BBCart.addBundle attaches the
    // product's free gifts (from the same cro.gift_* metafields the PDP reads),
    // de-dupes gifts already in the cart, and dispatches cart:added /
    // bb:cart:open so bb-cart-drawer refreshes and slides open — i.e. adding
    // from the results page behaves exactly like adding anywhere else on site.
    // Falls back to a plain add if that script hasn't loaded, so the quiz still
    // works standalone.
    var done = function () {
      state.added[pid] = true;
      render();
      syncCartCount();
    };
    var failed = function () {
      state.cartError = 'We couldn’t add that to your cart. Please try again.';
      render();
    };

    if (window.BBCart && typeof window.BBCart.addBundle === 'function') {
      window.BBCart.addBundle({
        variantId: p.variantId,
        bundleId: 'quiz-' + pid,
        giftIds: Array.isArray(p.giftIds) ? p.giftIds : [],
        qty: 1,
        properties: attribution(pid)
      })
        .then(function () { return stampCart(pid); })
        .then(done)['catch'](failed);
      return;
    }

    addItems([{
      id: p.variantId,
      quantity: 1,
      properties: attribution(pid)
    }], pid).then(done)['catch'](failed);
  }

  // Cart count for the floating button. The drawer broadcasts cart:refresh with
  // an item_count already corrected for shipping protection — the same number
  // the site header badge shows — so prefer that and only fetch as a fallback.
  // Derive the per-product "Added" state from the cart itself rather than
  // remembering that we once added it. Otherwise removing a line in the drawer
  // leaves the button stuck on "Added ✓" and disabled, with no way back.
  function reconcileFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;

    var inCart = {};
    cart.items.forEach(function (it) {
      if (it && it.variant_id != null) inCart[String(it.variant_id)] = true;
    });

    var changed = false;
    Object.keys(PRODUCTS).forEach(function (pid) {
      var vid = PRODUCTS[pid] && PRODUCTS[pid].variantId;
      var present = vid != null && !!inCart[String(vid)];
      if (present !== !!state.added[pid]) {
        if (present) state.added[pid] = true; else delete state.added[pid];
        changed = true;
      }
    });

    if (typeof cart.item_count === 'number' && cart.item_count !== state.cartCount) {
      state.cartCount = cart.item_count;
      changed = true;
    }
    return changed;
  }

  function syncCartCount() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (reconcileFromCart(cart) && state.screen === 'results') render();
      })['catch'](function () {});
  }

  // The drawer broadcasts this after every change — including removals — with a
  // protection-corrected item_count, the same number the site header badge uses.
  document.addEventListener('cart:refresh', function (ev) {
    var cart = ev && ev.detail;
    if (!cart) return;
    var changed = reconcileFromCart(cart);
    // item_count alone still counts even if the payload carried no items array.
    if (!changed && typeof cart.item_count === 'number' && cart.item_count !== state.cartCount) {
      state.cartCount = cart.item_count;
      changed = true;
    }
    if (changed && state.screen === 'results') render();
  });

  /* ================================================ seam 5 — email transport */

  function validateEmail(raw) {
    var e = (raw || '').trim().toLowerCase();
    if (!e) return 'Enter your email so we can send your plan.';
    var m = e.match(/^[^\s@]+@([^\s@]+\.[^\s@]+)$/);
    if (!m) return 'That email doesn’t look quite right — mind checking it?';
    var domain = m[1];
    var disposable = ['mailinator.com', 'guerrillamail.com', '10minutemail.com', 'tempmail.com', 'temp-mail.org', 'trashmail.com', 'yopmail.com', 'sharklasers.com', 'getnada.com', 'maildrop.cc', 'fakeinbox.com', 'throwawaymail.com', 'dispostable.com', 'guerrillamail.info'];
    for (var i = 0; i < disposable.length; i++) {
      if (domain === disposable[i] || domain.endsWith('.' + disposable[i])) {
        return 'Please use a permanent inbox — a temporary email won’t receive your plan.';
      }
    }
    var typos = { 'gmial.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gnail.com': 'gmail.com', 'gmail.co': 'gmail.com', 'gmaill.com': 'gmail.com', 'hotnail.com': 'hotmail.com', 'hotmial.com': 'hotmail.com', 'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com', 'outlok.com': 'outlook.com', 'iclod.com': 'icloud.com', 'icloud.co': 'icloud.com' };
    if (typos[domain]) return 'Did you mean @' + typos[domain] + '?';
    return '';
  }

  function defaultOnEmailSubmit(payload) {
    if (!API_BASE || !hasConsent()) return;
    try {
      fetch(API_BASE + '/v1/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'omit',
        mode: 'cors'
        // If a Turnstile challenge is added later, the token rides on `payload`.
      })['catch'](function () {});
    } catch (e) {}
  }
  if (typeof window.BB_QUIZ.onEmailSubmit !== 'function') {
    window.BB_QUIZ.onEmailSubmit = defaultOnEmailSubmit;
  }

  function submitEmail() {
    if (!state.firstName.trim()) {
      state.emailError = 'Add your first name so we can personalize your plan.';
      render();
      return;
    }
    var err = validateEmail(state.email);
    if (err) {
      state.emailError = err;
      track('email_error', { reason: err });
      render();
      return;
    }
    var r = computeResults();
    var payload = {
      quizId: quizId,
      visitorId: visitorId,
      firstName: state.firstName.trim(),
      email: state.email.trim().toLowerCase(),
      phone: state.phone.trim() || null,
      marketingConsent: !!state.consent,
      products: r.recs.filter(function (x) { return x.inPlan; }).map(function (x) { return x.pid; }),
      profile: r.pct,
      answers: state.answers,

      // --- 1:1 email payload -------------------------------------------------
      // The results email must show exactly what this page showed. The rule is:
      // the storefront renders it, so the storefront sends it — Klaviyo derives
      // nothing, sorts nothing, and re-words nothing. Anything hand-authored on
      // the Klaviyo side drifts the moment either end is edited.
      topArea: (r.ranked && r.ranked.length) ? r.ranked[0] : null,
      resultsTitle: r.resultsTitle,
      overview: r.overview,
      needLevel: r.needLevel,

      // Pre-sorted, high→low, winner first. This has to be an ARRAY: Liquid
      // cannot sort an object by value, which is why the first test email put
      // "Top match" on a bar sitting third.
      areas: (r.ranked || []).map(function (key, i) {
        return {
          key: key,
          label: AREA_NAME[key] || key,   // the label the bars actually render
          pct: r.pct[key],
          top: i === 0 && r.pct[key] > 0
        };
      }),

      // EVERY rec shown, not just the inPlan subset — the email leads with the
      // plan via the inPlan flag rather than silently dropping the rest.
      // Prices come from the live Shopify product object, so the email's
      // discount line can never disagree with what checkout charges.
      recommendations: r.recs.map(function (x) {
        var p = product(x.pid);
        return {
          pid: x.pid,
          rank: x.rank,
          inPlan: !!x.inPlan,
          name: p.name || x.pid,
          // PRE-FORMATTED to two decimals, digits only — the Klaviyo template
          // supplies the "$". Klaviyo Liquid prints whatever it is handed, so a raw
          // number rendered "$34" where the results page said "$34.00", and would
          // have rendered "$27.2" the first time a discount landed on an uneven
          // amount. Prices come live from Shopify, so that was a matter of when.
          //
          // Formatting here rather than in the template is deliberate: it needs no
          // edit to the live email template, and it keeps the rule this whole
          // integration runs on — the storefront renders it, so the storefront
          // sends it. The raw numbers stay available below for anything computing.
          price: money2(p.price),
          compareAt: typeof p.compareAt === 'number' ? money2(p.compareAt) : null,
          priceValue: typeof p.price === 'number' ? p.price : null,
          compareAtValue: typeof p.compareAt === 'number' ? p.compareAt : null,
          days: p.days || null,
          badge: x.badge || null,
          headline: x.headline,
          reasons: x.reasons || [],
          url: emailPdpUrl(x.pid),
          // Shopify's image_url is protocol-relative ('//cdn…'). A browser fills
          // the protocol in from the page it is on; an email client has no page,
          // so <img src="//…"> silently fails to load in most of them.
          image: absUrl(p.image)
        };
      }),

      ts: Date.now()
    };
    track('email_submitted', {
      step: 'results', hasPhone: !!payload.phone,
      consent: payload.marketingConsent, products: payload.products
    });
    try { if (typeof window.BB_QUIZ.onEmailSubmit === 'function') window.BB_QUIZ.onEmailSubmit(payload); } catch (e) {}
    state.emailSent = true;
    state.emailError = '';
    render();
  }

  /* ============================================================== navigation */

  function qMeta() {
    var qTotal = FLOW.filter(function (f) { return f.type === 'q'; }).length;
    var qDone = FLOW.slice(0, state.stepIdx + 1).filter(function (f) { return f.type === 'q'; }).length;
    return { total: qTotal, done: qDone, pct: Math.round((qDone / qTotal) * 100) };
  }

  function start() {
    state.screen = 'quiz';
    state.stepIdx = 0;
    state.startedAt = Date.now();
    var step = FLOW[0];
    if (step && step.type === 'info') track('info_viewed', { key: step.key });
    render();
  }

  function back() {
    if (state.stepIdx === 0) { state.screen = 'intro'; render(); return; }
    state.stepIdx -= 1;
    render();
  }

  function advance() {
    var n = state.stepIdx + 1;
    if (n >= FLOW.length) { finish(); return; }
    var step = FLOW[n];
    if (step.type === 'info') track('info_viewed', { key: step.key });
    state.stepIdx = n;
    render();
  }

  function finish() {
    var r = computeResults();
    var topArea = r.ranked && r.ranked.length ? r.ranked[0] : null;
    track('quiz_completed', {
      answers: state.answers,
      profile: r.pct,
      topArea: topArea,
      durationMs: state.startedAt ? Date.now() - state.startedAt : null
    });
    // Every product shown, with rank — the impression denominator for per-product CTR.
    track('recommendation_shown', {
      products: r.recs.map(function (x) { return { pid: x.pid, rank: x.rank, inPlan: !!x.inPlan }; }),
      needLevel: r.needLevel
    });
    state.screen = 'results';
    state.resultsAt = Date.now();
    render();
    // Reflect anything already in the cart when they land here.
    syncCartCount();
  }

  // Reflect the current answer on the buttons already in the DOM. Used instead
  // of a full render() while the visitor is still on the same question, so
  // picking/unpicking an option never rebuilds the screen (a rebuild replayed
  // the entry animation and re-painted the illustration — the "flash" on every
  // checkbox tap).
  function syncOptions() {
    var step = FLOW[state.stepIdx];
    if (!step || step.type !== 'q') return;
    var q = qById(step.qid);
    if (!q) return;
    var ans = state.answers[q.id];

    var nodes = app.querySelectorAll('.bbq-opt');
    for (var i = 0; i < nodes.length; i++) {
      var id = nodes[i].getAttribute('data-opt');
      var sel = q.multi
        ? (Array.isArray(ans) && ans.indexOf(id) >= 0)
        : (ans === id);
      if (sel) nodes[i].classList.add('is-selected');
      else nodes[i].classList.remove('is-selected');
      nodes[i].setAttribute('aria-pressed', sel ? 'true' : 'false');
    }

    if (q.multi) {
      var cont = app.querySelector('[data-act="continue"]');
      if (cont) {
        var has = Array.isArray(ans) && ans.length > 0;
        if (has) cont.classList.add('is-ready'); else cont.classList.remove('is-ready');
        cont.disabled = !has;
      }
    }
  }

  function selectSingle(qid, optId) {
    state.answers[qid] = optId;
    track('question_answered', { qid: qid, value: optId });
    syncOptions();          // show the tick in place…
    setTimeout(advance, 240); // …then the screen change animates
  }

  function toggleMulti(qid, opt) {
    var q = qById(qid);
    var cur = Array.isArray(state.answers[qid]) ? state.answers[qid].slice() : [];
    if (opt.exclusive) {
      cur = cur.indexOf(opt.id) >= 0 ? [] : [opt.id];
    } else {
      cur = cur.filter(function (id) {
        var o = null;
        for (var i = 0; i < q.options.length; i++) if (q.options[i].id === id) { o = q.options[i]; break; }
        return o && !o.exclusive;
      });
      var at = cur.indexOf(opt.id);
      if (at >= 0) cur.splice(at, 1); else cur.push(opt.id);
    }
    state.answers[qid] = cur;
    syncOptions(); // in place — no re-render, no animation replay
  }

  function continueStep() {
    var step = FLOW[state.stepIdx];
    if (step.type === 'q') {
      track('question_answered', { qid: step.qid, value: state.answers[step.qid] || [] });
    }
    advance();
  }

  function retake() {
    track('quiz_retake', {});
    if (state.resultsAt) {
      track('results_dwell', { qid: null, ms: Date.now() - state.resultsAt });
      state.resultsAt = 0;
    }
    quizId = newQuizId();
    window.BB_QUIZ.quizId = quizId;
    state.screen = 'intro';
    state.stepIdx = 0;
    state.answers = {};
    state.added = {};
    state.cartCount = 0;
    state.emailSent = false;
    state.emailError = '';
    state.emailOpen = false;
    state.showExit = false;
    state.cartError = '';
    state.introVariant = pickIntroVariant();
    render();
  }

  function confirmExit() {
    // Same event, same dedupe flag as the leave handler — an explicit "Leave the
    // quiz" followed by the pagehide it causes must not log two abandons.
    markAbandoned();
    flush(true);
    window.location.href = CONFIG.homeUrl;
  }

  /* ================================================================ render */

  function progressHTML(onColor) {
    var m = qMeta();
    return '<div class="bbq-progress' + (onColor ? ' bbq-progress--onColor' : '') + '">' +
      '<div class="bbq-progress__bar" style="width:' + m.pct + '%"></div></div>';
  }

  function renderIntro() {
    var intro = INTROS[state.introVariant] || INTROS[0];
    var hero = resolveArt(ART.homeHero, '');
    var logo = resolveArt(ART.logo, '');
    return '' +
      '<div class="bbq-screen bbq-intro">' +
        // Wordmark falls back to the text lockup if the logo is missing or its
        // URL fails the origin check — the intro must never render a blank
        // header just because an asset moved.
        '<div class="bbq-intro__brand">' +
          (logo
            ? '<img class="bbq-intro__logo" src="' + esc(logo) + '" alt="Boost Blenz" width="150" height="44" fetchpriority="high" decoding="async">'
            : '<span class="bbq-intro__brandname">Boost Blenz</span>') +
        '</div>' +
        '<div class="bbq-intro__copy">' +
          '<p class="bbq-intro__eyebrow">' + esc(intro.eyebrow) + '</p>' +
          '<h1 class="bbq-intro__title">' + esc(intro.title) + '</h1>' +
          '<p class="bbq-intro__sub">' + esc(intro.sub) + '</p>' +
          '<button type="button" class="bbq-btn bbq-btn--orange bbq-intro__cta" data-act="start">' + esc(intro.cta) + '</button>' +
          '<p class="bbq-intro__note">' + esc(intro.note) + '</p>' +
        '</div>' +
        // Mascot lives in its own region BELOW the copy (never under the text),
        // filling the leftover space and bleeding off the edges to hold a big
        // size — off the bottom on desktop, off the sides too on phones. The
        // wrapper owns the region; the img covers it. Decorative, hence empty alt.
        (hero ? '<div class="bbq-intro__herowrap"><img class="bbq-intro__hero" src="' + esc(hero) + '" alt="" fetchpriority="high" decoding="async"></div>' : '') +
      '</div>';
  }

  function renderInfo(step) {
    var info = INFO[step.key];
    if (!info) return '';
    var illo = slotArt('info_' + step.key, info.illo);
    return '' +
      '<div class="bbq-screen bbq-info" style="background:' + esc(info.bg) + ';color:' + esc(info.ink) + '">' +
        // Back sits at the TOP here, in the same place as on question screens.
        // It used to live in a footer at the very bottom of a 100vh card, so it
        // was both easy to miss and the first thing to fall below the fold on a
        // short viewport — shoppers looked top-left, saw nothing, and concluded
        // the quiz was one-way.
        '<div class="bbq-info__progress-wrap">' +
          '<div class="bbq-info__headrow">' +
            '<button type="button" class="bbq-info__back" data-act="back" style="color:' + esc(info.ink) + '">&larr; ' + esc(UI.backLabel) + '</button>' +
          '</div>' +
          progressHTML(true) +
        '</div>' +
        '<div class="bbq-info__body">' +
          '<div class="bbq-info__tile">' +
            '<div class="bbq-info__illo" role="img" aria-label="' + esc(info.title) + '"' +
              (illo ? ' style="background-image:url(' + esc(illo) + ')"' : '') + '></div>' +
          '</div>' +
          '<p class="bbq-info__eyebrow">' + esc(info.eyebrow) + '</p>' +
          '<h2 class="bbq-info__title">' + esc(info.title) + '</h2>' +
          '<p class="bbq-info__text">' + esc(info.body) + '</p>' +
          '<button type="button" class="bbq-btn bbq-btn--dark" data-act="continue">' + esc(info.cta) + '</button>' +
        '</div>' +
      '</div>';
  }

  function renderQuestion(step) {
    var q = qById(step.qid);
    if (!q) return '';
    var th = THEMES[q.theme] || THEMES.general;
    var m = qMeta();
    var ans = state.answers[q.id];
    var img = slotArt('q_' + q.id, q.image);

    var opts = q.options.map(function (opt, i) {
      var selected = q.multi
        ? (Array.isArray(ans) && ans.indexOf(opt.id) >= 0)
        : (ans === opt.id);
      return '' +
        '<button type="button" class="bbq-opt' + (selected ? ' is-selected' : '') + '"' +
          ' data-act="opt" data-qid="' + esc(q.id) + '" data-opt="' + esc(opt.id) + '"' +
          ' aria-pressed="' + (selected ? 'true' : 'false') + '">' +
          '<span class="bbq-opt__num">' + ('0' + (i + 1)).slice(-2) + '</span>' +
          '<span class="bbq-opt__label">' + esc(opt.label) + '</span>' +
          '<span class="bbq-opt__check" aria-hidden="true">✓</span>' +
        '</button>';
    }).join('');

    var has = q.multi ? (Array.isArray(ans) && ans.length > 0) : (ans != null);
    var isLast = state.stepIdx >= FLOW.length - 1;

    return '' +
      '<div class="bbq-screen bbq-q">' +
        '<div class="bbq-q__band" style="background:' + esc(th.bg) + ';color:' + esc(th.ink) + '">' +
          // Eyebrow defaults to the THEME label, which every question of that
          // theme shares. q.eyebrow overrides it per question so each panel can
          // be worded for its own question rather than for its group.
          '<span class="bbq-q__bandlabel">' + esc(q.eyebrow || th.label) + '</span>' +
          '<div class="bbq-q__bandtile">' +
            '<div class="bbq-q__bandimg" role="img" aria-label="' + esc(q.caption || q.eyebrow || th.label) + '"' +
              (img ? ' style="background-image:url(' + esc(img) + ')"' : '') + '></div>' +
          '</div>' +
          '<p class="bbq-q__caption">' + esc(q.caption) + '</p>' +
        '</div>' +
        '<div class="bbq-q__main">' +
          '<div class="bbq-q__inner">' +
            '<div class="bbq-q__head">' +
              '<div class="bbq-q__headrow">' +
                '<button type="button" class="bbq-back" data-act="back">&larr; ' + esc(UI.backLabel) + '</button>' +
                '<span class="bbq-q__count">' + esc(UI.progressWord) + ' ' + m.done + ' of ' + m.total + '</span>' +
              '</div>' +
              progressHTML(false) +
            '</div>' +
            '<div class="bbq-q__anim">' +
              '<h2 class="bbq-q__title">' + esc(q.title) + '</h2>' +
              '<p class="bbq-q__sub">' + esc(q.sub) + '</p>' +
              '<div class="bbq-q__options" role="group" aria-label="' + esc(q.title) + '">' + opts + '</div>' +
              '<div class="bbq-q__foot">' +
                (q.multi
                  ? '<button type="button" class="bbq-continue' + (has ? ' is-ready' : '') + '"' +
                    ' data-act="continue"' + (has ? '' : ' disabled') + '>' +
                    (isLast ? esc(UI.lastStepLabel) : esc(UI.continueLabel)) + '</button>'
                  : '') +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function incentiveLines() {
    var mode = CONFIG.incentiveMode || 'none';
    if (mode === 'none') return [];
    var lines = [];
    if (mode === 'percent' || mode === 'both') {
      lines.push({
        big: CONFIG.incentivePercent + '% OFF',
        small: 'your recommended detox — use code ' + CONFIG.incentiveCode + ' at checkout'
      });
    }
    if (mode === 'shipping' || mode === 'both') {
      lines.push({ big: 'FREE SHIPPING', small: 'on orders over $' + CONFIG.shippingThreshold });
    }
    return lines;
  }

  function renderResults() {
    var r = computeResults();
    var lines = incentiveLines();

    var chart = r.ranked.map(function (area, i) {
      var val = r.pct[area];
      var isTop = i === 0 && val > 0;
      return '' +
        '<div class="bbq-bar-row">' +
          '<div class="bbq-bar-head">' +
            '<span class="bbq-bar-name-wrap">' +
              '<span class="bbq-bar-name">' + esc(AREA_NAME[area] || area) + '</span>' +
              (isTop ? '<span class="bbq-badge-top">' + esc(UI.topMatch) + '</span>' : '') +
            '</span>' +
            '<span class="bbq-bar-pct' + (isTop ? ' is-top' : '') + '">' + val + '%</span>' +
          '</div>' +
          '<div class="bbq-bar-track">' +
            '<div class="bbq-bar-fill' + (isTop ? ' is-top' : '') + '"' +
              ' style="width:' + (val > 0 ? Math.max(val, 4) : 0) + '%;opacity:' +
              (isTop ? 1 : Math.max(0.4, 0.9 - i * 0.16)) + '"></div>' +
          '</div>' +
        '</div>';
    }).join('');

    var recsHTML = r.recs.map(function (item) {
      var p = product(item.pid);
      var isKit = item.pid === 'kit';
      var added = !!state.added[item.pid];
      var img = safeImg(p.image, p.fallbackImage);

      // Every card shows its own product photo — including the kit, which has a
      // real product image of its own. (It used to render a 2x2 collage of the
      // four component photos, which wasn't the kit's actual image.)
      var media = img
        ? '<img class="bbq-rec__img" src="' + esc(img) + '" alt="' + esc(p.name) + '" loading="lazy" decoding="async">'
        : '';

      var chips = (item.reasons || []).map(function (x) {
        return '<span class="bbq-chip">' + esc(x) + '</span>';
      }).join('');

      return '' +
        '<div class="bbq-rec' + (isKit ? ' bbq-rec--kit' : '') + '">' +
          '<div class="bbq-rec__media">' + media + '</div>' +
          '<div class="bbq-rec__body">' +
            '<div class="bbq-rec__toprow">' +
              '<span class="bbq-rec__rank">' + item.rank + '</span>' +
              (item.badge ? '<span class="bbq-rec__badge">' + esc(item.badge) + '</span>' : '') +
            '</div>' +
            '<h3 class="bbq-rec__name">' + esc(p.name) + '</h3>' +
            '<div class="bbq-rec__pricerow">' +
              '<span class="bbq-rec__price">' + esc(money(p.price)) + '</span>' +
              (p.compareAt ? '<span class="bbq-rec__compare">' + esc(money(p.compareAt)) + '</span>' : '') +
              '<span class="bbq-rec__days">' + esc(p.days) + '</span>' +
            '</div>' +
            '<p class="bbq-rec__headline">' + esc(item.headline) + '</p>' +
            '<div class="bbq-rec__reasons">' + chips + '</div>' +
            '<div class="bbq-rec__actions">' +
              '<button type="button" class="bbq-btn bbq-rec__add' + (added ? ' bbq-btn--added' : '') + '"' +
                ' data-act="add" data-pid="' + esc(item.pid) + '"' + (added ? ' disabled' : '') + '>' +
                (added ? esc(UI.addedLabel) + ' ✓' : esc(UI.addToCart)) + '</button>' +
              '<a class="bbq-btn bbq-btn--ghost" href="' + esc(pdpUrl(item.pid)) + '"' +
                ' target="_blank" rel="noopener" data-act="view" data-pid="' + esc(item.pid) + '">' + esc(UI.viewProduct) + '</a>' +
            '</div>' +
          '</div>' +
        '</div>';
    }).join('');

    var emailBlock = state.emailSent
      ? '<div class="bbq-email-sent">' +
          '<span class="bbq-email-sent__title">' + esc(UI.emailConfirmTitle) + ' ✓</span>' +
          '<p class="bbq-email-sent__text">' + esc(UI.emailConfirmPrefix) +
            esc(state.email.trim().toLowerCase() || UI.emailConfirmFallback) +
            esc(UI.emailConfirmSuffix) + '</p>' +
        '</div>'
      : '<div class="bbq-email-toggle-wrap">' +
          '<button type="button" class="bbq-btn bbq-email-toggle" data-act="toggle-email">' +
            (state.emailOpen ? esc(UI.emailHide) + ' ▲' : esc(UI.emailCta) + ' ▼') +
          '</button>' +
        '</div>' +
        '<div class="bbq-email-panel' + (state.emailOpen ? ' is-open' : '') + '">' +
          '<div class="bbq-email-card">' +
            '<div class="bbq-email-card__head">' +
              '<span class="bbq-email-card__headtext">' + esc(UI.emailHeading) + '</span>' +
            '</div>' +
            '<div class="bbq-email-card__body">' +
              '<p class="bbq-email__intro">' + esc(UI.emailIntro) + '</p>' +
              '<div class="bbq-email__row">' +
                '<label class="bbq-field bbq-field--name">' +
                  '<span class="bbq-field__label">' + esc(UI.emailFirstLabel) + '</span>' +
                  '<input class="bbq-input" type="text" name="bbq-first" autocomplete="given-name" placeholder="Jordan" value="' + esc(state.firstName) + '" data-field="firstName">' +
                '</label>' +
                '<label class="bbq-field bbq-field--email">' +
                  '<span class="bbq-field__label">' + esc(UI.emailEmailLabel) + '</span>' +
                  '<input class="bbq-input" type="email" name="bbq-email" autocomplete="email" placeholder="you@email.com" value="' + esc(state.email) + '" data-field="email">' +
                '</label>' +
              '</div>' +
              '<label class="bbq-field bbq-field--phone">' +
                '<span class="bbq-field__label">' + esc(UI.emailPhoneLabel) + ' <span class="bbq-field__hint">' + esc(UI.emailPhoneHint) + '</span></span>' +
                '<input class="bbq-input" type="tel" name="bbq-phone" autocomplete="tel" placeholder="(555) 555-5555" value="' + esc(state.phone) + '" data-field="phone">' +
              '</label>' +
              '<label class="bbq-consent">' +
                '<input type="checkbox" data-field="consent"' + (state.consent ? ' checked' : '') + '>' +
                '<span class="bbq-consent__text">' + esc(UI.emailConsent) + '</span>' +
              '</label>' +
              (state.emailError ? '<div class="bbq-email__error" role="alert">' + esc(state.emailError) + '</div>' : '') +
              '<div class="bbq-email__submit-wrap">' +
                '<button type="button" class="bbq-btn bbq-btn--purple" data-act="submit-email">' + esc(UI.emailButton) + '</button>' +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>';

    return '' +
      '<div class="bbq-screen bbq-results">' +
        emailBlock +
        '<div class="bbq-card">' +
          '<p class="bbq-card__label">' + esc(UI.resultsMatchLabel) + '</p>' +
          chart +
          '<div class="bbq-how">' +
            '<p class="bbq-how__label">' + esc(UI.scoreHowLabel) + '</p>' +
            '<p class="bbq-how__text">' + esc(UI.scoreHowText) + '</p>' +
          '</div>' +
        '</div>' +
        '<div class="bbq-profile">' +
          '<div class="bbq-profile__head">' +
            '<p class="bbq-profile__eyebrow">' + esc(UI.profileEyebrow) + '</p>' +
            '<h1 class="bbq-profile__title">' + esc(r.resultsTitle) + '</h1>' +
          '</div>' +
          '<div class="bbq-profile__body">' +
            '<p class="bbq-profile__label">' + esc(UI.profileLabel) + '</p>' +
            '<p class="bbq-profile__text">' + esc(r.overview) + '</p>' +
          '</div>' +
        '</div>' +
        (lines.length
          ? '<div class="bbq-incentives">' + lines.map(function (l) {
              return '<div class="bbq-incentive">' +
                '<span class="bbq-incentive__big">' + esc(l.big) + '</span>' +
                '<span class="bbq-incentive__small">' + esc(l.small) + '</span></div>';
            }).join('') + '</div>'
          : '') +
        '<p class="bbq-recs-label">' + esc(UI.recsLabel) + '</p>' +
        '<div class="bbq-recs">' + recsHTML + '</div>' +
        (state.cartError ? '<p class="bbq-rec__err" role="alert">' + esc(state.cartError) + '</p>' : '') +
        '<div class="bbq-retake-wrap">' +
          '<button type="button" class="bbq-link" data-act="retake">' + esc(UI.startOver) + '</button>' +
        '</div>' +
        '<div class="bbq-disclaimer-wrap"><p class="bbq-disclaimer">' + esc(UI.disclaimer) + '</p></div>' +
      '</div>' +
      '';
  }

  // Floating cart button — the results page has no site header, so this is the
  // shopper's way into the cart. Opens the shared drawer via the same event the
  // header uses.
  function renderCartButton() {
    var n = state.cartCount;
    return '' +
      '<button type="button" class="bbq-cart-btn" data-act="open-cart"' +
        ' aria-label="Open cart' + (n ? ', ' + n + ' item' + (n === 1 ? '' : 's') : '') + '">' +
        '<svg class="bbq-cart-btn__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
          '<path d="M6 7h12l-1 12H7L6 7Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>' +
          '<path d="M9 7a3 3 0 0 1 6 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
        '</svg>' +
        (n > 0 ? '<span class="bbq-cart-btn__count">' + n + '</span>' : '') +
      '</button>';
  }

  function renderExitSheet() {
    return '' +
      '<div class="bbq-sheet" role="dialog" aria-modal="true" aria-label="' + esc(UI.exitTitle) + '">' +
        '<button type="button" class="bbq-sheet__scrim" data-act="close-exit" aria-label="Close"></button>' +
        '<div class="bbq-sheet__panel">' +
          '<div class="bbq-sheet__grab"></div>' +
          '<h3 class="bbq-sheet__title">' + esc(UI.exitTitle) + '</h3>' +
          '<p class="bbq-sheet__text">' + esc(UI.exitBody) + '</p>' +
          '<div class="bbq-sheet__actions">' +
            '<button type="button" class="bbq-btn bbq-btn--purple bbq-sheet__stay" data-act="close-exit">' + esc(UI.exitStay) + '</button>' +
            // Middle option. Leaving was previously all-or-nothing: someone who
            // just wanted a clean run had to abandon to the home page and find
            // their way back in. retake() already existed for the results page.
            '<button type="button" class="bbq-sheet__restart" data-act="restart-quiz">' + esc(UI.exitRestart) + '</button>' +
            '<button type="button" class="bbq-sheet__go" data-act="confirm-exit">' + esc(UI.exitLeave) + '</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  var lastQuestionShown = '';
  // Writes text only when it actually differs, so the untouched variant causes no
  // DOM mutation at all — not even an invalidation the browser has to consider.
  function setTextIfChanged(root, sel, value) {
    var el = root.querySelector(sel);
    if (!el || typeof value !== 'string') return;
    if (el.textContent.trim() !== value) el.textContent = value;
  }

  var lastScreenKey = '';
  // The first render replaces server-rendered markup that is ALREADY on screen.
  // Playing the entrance animation over it re-animated content the visitor could
  // see, which read as a flash on load. Only real screen changes animate.
  var firstRender = true;

  function render() {
    var html = '';
    var inQuiz = state.screen === 'quiz';
    var step = FLOW[state.stepIdx] || FLOW[0];

    // Identifies the screen the visitor is looking at. Re-rendering the same
    // screen (add-to-cart, email toggle, an error message) must not animate;
    // only an actual move between screens does.
    var screenKey = state.screen + ':' + (inQuiz ? state.stepIdx : '-');
    var screenChanged = screenKey !== lastScreenKey;

    // FIRST PAINT ON THE INTRO — patch, don't rebuild.
    //
    // The server already rendered the intro, and since that markup mirrors
    // renderIntro() element for element, the only thing that can differ is which
    // A/B variant's copy it holds (Liquid can't read the visitor cookie, so it
    // always renders variant 0). Replacing innerHTML to change four strings tears
    // down and re-creates the logo and mascot <img>s — that re-decode is the flash.
    //
    // So on the very first render we write the text into the nodes that are
    // already on screen. Variant 0 changes nothing at all; variants 1 and 2 get a
    // single text repaint with no layout teardown. The server-rendered CTA carries
    // data-act="start", and clicks are delegated, so the node is already live.
    if (firstRender && state.screen === 'intro' && !state.showExit) {
      var ssr = app.querySelector('[data-bbq-ssr-intro]');
      if (ssr) {
        var v = INTROS[state.introVariant] || INTROS[0];
        setTextIfChanged(ssr, '.bbq-intro__eyebrow', v.eyebrow);
        setTextIfChanged(ssr, '.bbq-intro__title', v.title);
        setTextIfChanged(ssr, '.bbq-intro__sub', v.sub);
        setTextIfChanged(ssr, '.bbq-intro__cta', v.cta);
        setTextIfChanged(ssr, '.bbq-intro__note', v.note);
        lastScreenKey = screenKey;
        lastQuestionShown = '';
        firstRender = false;
        return;
      }
    }

    // The intro had no exit at all, so anyone arriving from a site link was
    // stuck with the browser back button. It leaves STRAIGHT to the site rather
    // than opening the sheet: there are no answers to lose yet, and asking
    // "your answers won't be saved" when there are none is nonsense friction.
    if (state.screen === 'intro') {
      html += '<button type="button" class="bbq-exit-btn" data-act="leave-site" aria-label="Back to Boost Blenz">✕</button>';
    } else if (inQuiz) {
      html += '<button type="button" class="bbq-exit-btn" data-act="open-exit" aria-label="Exit quiz">✕</button>';
    }

    if (state.screen === 'intro') {
      html += renderIntro();
    } else if (inQuiz && step.type === 'info') {
      html += renderInfo(step);
    } else if (inQuiz && step.type === 'q') {
      html += renderQuestion(step);
    } else if (state.screen === 'results') {
      html += renderCartButton();
      html += renderResults();
    }

    if (state.showExit) html += renderExitSheet();

    app.innerHTML = html;
    lastScreenKey = screenKey;

    if (screenChanged && !firstRender) {
      var screenEl = app.querySelector('.bbq-screen');
      if (screenEl) screenEl.classList.add('bbq-anim-in');
    }
    firstRender = false;

    // question_shown gives a true drop-off denominator (shown vs answered).
    if (inQuiz && step.type === 'q') {
      var key = step.qid + '@' + state.stepIdx;
      if (key !== lastQuestionShown) {
        lastQuestionShown = key;
        track('question_shown', { qid: step.qid, stepIdx: state.stepIdx });
      }
    } else if (!inQuiz) {
      lastQuestionShown = '';
    }

    if (state.screen === 'results') {
      var lines = incentiveLines();
      if (lines.length && !render._incentiveTracked) {
        render._incentiveTracked = true;
        track('incentive_shown', { mode: CONFIG.incentiveMode, code: CONFIG.incentiveCode });
      }
    }
  }

  /* ============================================================ delegation */

  app.addEventListener('click', function (ev) {
    var el = ev.target.closest('[data-act]');
    if (!el) return;
    var act = el.getAttribute('data-act');

    switch (act) {
      case 'start':
        ev.preventDefault();
        start();
        break;
      case 'back':
        ev.preventDefault();
        back();
        break;
      case 'continue':
        ev.preventDefault();
        continueStep();
        break;
      case 'opt': {
        ev.preventDefault();
        var qid = el.getAttribute('data-qid');
        var optId = el.getAttribute('data-opt');
        var q = qById(qid);
        if (!q) break;
        var opt = null;
        for (var i = 0; i < q.options.length; i++) if (q.options[i].id === optId) { opt = q.options[i]; break; }
        if (!opt) break;
        if (q.multi) toggleMulti(qid, opt); else selectSingle(qid, optId);
        break;
      }
      case 'add':
        ev.preventDefault();
        addToCart(el.getAttribute('data-pid'));
        break;
      case 'view':
        // Let the link open normally; just record it.
        track('product_view', { product: el.getAttribute('data-pid'), pdpUrl: el.getAttribute('href') });
        break;
      case 'open-cart':
        ev.preventDefault();
        track('cart_opened', { cartCount: state.cartCount });
        // Same event the site header fires; bb-cart-drawer listens for it.
        document.dispatchEvent(new CustomEvent('bb:cart:open'));
        break;
      case 'toggle-email': {
        ev.preventDefault();
        state.emailOpen = !state.emailOpen;
        // Toggled in place: re-rendering would build the panel already-open and
        // the max-height transition would never run, killing the slide.
        var panel = app.querySelector('.bbq-email-panel');
        if (panel) {
          if (state.emailOpen) panel.classList.add('is-open');
          else panel.classList.remove('is-open');
          el.textContent = state.emailOpen ? UI.emailHide + ' ▲' : UI.emailCta + ' ▼';
        } else {
          render();
        }
        if (state.emailOpen) track('email_form_opened', {});
        break;
      }
      case 'submit-email':
        ev.preventDefault();
        submitEmail();
        break;
      case 'retake':
        ev.preventDefault();
        retake();
        break;
      case 'open-exit':
        ev.preventDefault();
        state.showExit = true;
        render();
        break;
      case 'close-exit':
        ev.preventDefault();
        state.showExit = false;
        render();
        break;
      case 'leave-site':
        // Intro-screen exit. No answers exist yet, so this is not an abandon —
        // firing quiz_abandoned here would inflate the drop-off numbers with
        // people who never started.
        ev.preventDefault();
        flush(true);
        window.location.href = CONFIG.homeUrl;
        break;
      case 'restart-quiz':
        // Close the sheet first — retake() re-renders, and leaving showExit set
        // would redraw the sheet on top of a freshly restarted quiz.
        ev.preventDefault();
        state.showExit = false;
        retake();
        break;
      case 'confirm-exit':
        ev.preventDefault();
        confirmExit();
        break;
    }
  });

  // Keep field state without re-rendering on every keystroke (which would
  // blur the input); the panel only re-renders on submit/toggle.
  app.addEventListener('input', function (ev) {
    var f = ev.target.getAttribute && ev.target.getAttribute('data-field');
    if (!f) return;
    if (f === 'consent') { state.consent = !!ev.target.checked; return; }
    state[f] = ev.target.value;
    if (f === 'firstName' || f === 'email') state.emailError = '';
  });

  app.addEventListener('change', function (ev) {
    if (ev.target.getAttribute && ev.target.getAttribute('data-field') === 'consent') {
      state.consent = !!ev.target.checked;
    }
  });

  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape' && state.showExit) {
      state.showExit = false;
      render();
    }
  });

  window.BB_QUIZ.getState = function () {
    return { answers: state.answers, screen: state.screen, quizId: quizId, visitorId: visitorId };
  };

  /* ================================================================== boot */

  render();
  track('quiz_started', { introVariant: state.introVariant });
  track('intro_shown', { variant: state.introVariant });
  loadConfig();
})();
